import type { PostSource } from "../types";
import type { ApiWindow, AreaDetail, AreaFeedItem, EffectiveStatusValue } from "./apiTypes";
import {
  formatManilaClockRange,
  formatManilaDateTime,
  formatManilaWindow,
  manilaDayLabel,
  manilaDayStartMs, formatWindowDuration } from "./countdown";
import { type GlanceTier, SOON_THRESHOLD_MS } from "./glance";

export type SlotPhase = "past" | "now" | "future";

export interface SlotAlternative {
  kind: "sources_differ" | "revised";
  source: "facebook" | "portal_sheet" | null;
  start: string;
  end: string;
  localLabel?: string;
  postUrl: string | null;
  postCreatedAt: string | null;
}

export interface TimelineSlot {
  start: string;
  end: string;
  localLabel?: string;
  statusEffect: EffectiveStatusValue;
  kind: string;
  postUrl: string | null;
  source: "facebook" | "portal_sheet" | null;
  phase: SlotPhase;
  tier: GlanceTier;
  effectLabel: string;
  postCreatedAt: string | null;
  alternative: SlotAlternative | null;
}

interface RawSlot {
  start: string;
  end: string;
  startMs: number;
  endMs: number;
  localLabel?: string;
  statusEffect: EffectiveStatusValue;
  kind: string;
  postUrl: string | null;
  source: "facebook" | "portal_sheet" | null;
  postCreatedAt: string | null;
  alternative: SlotAlternative | null;
}

export function sourceLinkLabel(source: PostSource | null | undefined): string {
  return source === "portal_sheet" ? "VECO calendar" : "Facebook post";
}

const STATUS_EFFECT_LABELS: Record<EffectiveStatusValue, string> = {
  none: "No outage",
  possible: "Possible rotational outage",
  scheduled: "Scheduled outage",
  ongoing: "Outage ongoing",
  restored: "Restored",
  cancelled: "Cancelled",
  unknown: "Unknown",
};

function parseMs(iso: string): number | null {
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function effectLabelFor(kind: string, statusEffect: EffectiveStatusValue): string {
  const lower = kind.toLowerCase();
  if (lower.includes("emergency")) {
    return "Emergency outage";
  }
  if (lower.includes("maintenance")) {
    return "Maintenance";
  }
  return STATUS_EFFECT_LABELS[statusEffect] ?? "Unknown";
}

function phaseFor(startMs: number, endMs: number, nowMs: number): SlotPhase {
  if (startMs <= nowMs && nowMs < endMs) {
    return "now";
  }
  if (endMs <= nowMs) {
    return "past";
  }
  return "future";
}

function tierFor(phase: SlotPhase, startMs: number, nowMs: number): GlanceTier {
  if (phase === "past") {
    return "clear";
  }
  if (phase === "now") {
    return "off";
  }
  return startMs - nowMs <= SOON_THRESHOLD_MS ? "soon" : "later";
}

function manilaLocalDate(isoOrDate: string | Date): string {
  const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  return date.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
}

function itemSource(item: AreaFeedItem): "facebook" | "portal_sheet" {
  return item.source === "portal_sheet" ? "portal_sheet" : "facebook";
}

function isoGreater(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? "") > (b ?? "");
}

function collectPairings(items: readonly AreaFeedItem[]): Map<string, SlotAlternative> {
  const losers: AreaFeedItem[] = [];
  const candidates: AreaFeedItem[] = [];
  for (const item of items) {
    if (!item.window) {
      continue;
    }
    if (item.superseded === true && (item.supersededReason === "newer_source" || item.supersededReason === "sheet_revision")) {
      losers.push(item);
    }
    if (!item.superseded && !item.postMissing) {
      candidates.push(item);
    }
  }

  const byDate = new Map<string, AreaFeedItem[]>();
  for (const candidate of candidates) {
    const date = manilaLocalDate(candidate.window!.start);
    const list = byDate.get(date);
    if (list) {
      list.push(candidate);
    } else {
      byDate.set(date, [candidate]);
    }
  }

  const chosen = new Map<string, { alternative: SlotAlternative; loserCreatedAt: string | null }>();
  for (const loser of losers) {
    const window = loser.window;
    if (!window) {
      continue;
    }
    const sameDay = byDate.get(manilaLocalDate(window.start)) ?? [];
    const loserSource = itemSource(loser);
    const needDifferent = loser.supersededReason === "newer_source";
    let winner: AreaFeedItem | null = null;
    for (const candidate of sameDay) {
      const sameSource = itemSource(candidate) === loserSource;
      if (needDifferent ? sameSource : !sameSource) {
        continue;
      }
      if (!winner || isoGreater(candidate.postCreatedAt, winner.postCreatedAt)) {
        winner = candidate;
      }
    }
    if (!winner?.window) {
      continue;
    }
    const key = `${winner.window.start}|${winner.window.end}|${winner.statusEffect}|${itemSource(winner)}`;
    const alternative: SlotAlternative = {
      kind: loser.supersededReason === "newer_source" ? "sources_differ" : "revised",
      source: itemSource(loser),
      start: window.start,
      end: window.end,
      localLabel: window.localLabel,
      postUrl: loser.postUrl,
      postCreatedAt: loser.postCreatedAt,
    };
    const existing = chosen.get(key);
    if (!existing || isoGreater(loser.postCreatedAt, existing.loserCreatedAt)) {
      chosen.set(key, { alternative, loserCreatedAt: loser.postCreatedAt });
    }
  }

  const pairings = new Map<string, SlotAlternative>();
  for (const [key, value] of chosen) {
    pairings.set(key, value.alternative);
  }
  return pairings;
}

export function pairPanelId(start: string, index: number): string {
  return `pair-${start}-${index}`.toLowerCase().replace(/[^a-z0-9-]+/g, "-");
}

/** First chronological paired slot whose Manila local date is today or later. */
export function pairedSlotSummary(
  slots: readonly TimelineSlot[],
  now: Date,
): { kind: "sources_differ" | "revised"; slot: TimelineSlot } | null {
  const today = manilaLocalDate(now);
  for (const slot of slots) {
    if (!slot.alternative) {
      continue;
    }
    if (manilaLocalDate(slot.start) >= today) {
      return { kind: slot.alternative.kind, slot };
    }
  }
  return null;
}

/** Turns an area's feed items (plus its current/next status windows) into sorted, phased timeline slots. */
export function buildTimelineSlots(items: readonly AreaFeedItem[], detail: AreaDetail | null, now: Date): TimelineSlot[] {
  const bySlotKey = new Map<string, RawSlot>();
  const order: string[] = [];
  const startEndSeen = new Set<string>();
  const pairings = collectPairings(items);

  for (const item of items) {
    const window = item.window;
    if (!window || item.superseded || item.postMissing) {
      continue;
    }
    const startMs = parseMs(window.start);
    const endMs = parseMs(window.end);
    if (startMs === null || endMs === null) {
      continue;
    }
    const seKey = `${window.start}|${window.end}`;
    const source = item.source ?? "facebook";
    const key = `${seKey}|${item.statusEffect}|${source}`;
    const existing = bySlotKey.get(key);
    if (!existing) {
      bySlotKey.set(key, {
        start: window.start,
        end: window.end,
        startMs,
        endMs,
        localLabel: window.localLabel,
        statusEffect: item.statusEffect,
        kind: item.kind,
        postUrl: item.postUrl,
        source,
        postCreatedAt: item.postCreatedAt,
        alternative: pairings.get(key) ?? null,
      });
      order.push(key);
    } else if (!existing.postUrl && item.postUrl) {
      existing.postUrl = item.postUrl;
    }
    startEndSeen.add(seKey);
  }

  if (detail) {
    const active = detail.status.activeWindow;
    if (active) {
      const startMs = parseMs(active.start);
      const endMs = parseMs(active.end);
      const seKey = `${active.start}|${active.end}`;
      if (startMs !== null && endMs !== null && !startEndSeen.has(seKey)) {
        const key = `${seKey}|${detail.status.value}|${detail.status.source}`;
        bySlotKey.set(key, {
          start: active.start,
          end: active.end,
          startMs,
          endMs,
          localLabel: active.localLabel,
          statusEffect: detail.status.value,
          kind: "status_window",
          postUrl: detail.status.sourceUrl,
          source: detail.status.source,
          postCreatedAt: null,
          alternative: null,
        });
        order.push(key);
        startEndSeen.add(seKey);
      }
    }

    const next = detail.status.nextWindow;
    if (next) {
      const startMs = parseMs(next.start);
      const endMs = parseMs(next.end);
      const seKey = `${next.start}|${next.end}`;
      if (startMs !== null && endMs !== null && !startEndSeen.has(seKey)) {
        const effect: EffectiveStatusValue = detail.status.value === "scheduled" ? "scheduled" : "possible";
        const key = `${seKey}|${effect}|${detail.status.source}`;
        bySlotKey.set(key, {
          start: next.start,
          end: next.end,
          startMs,
          endMs,
          localLabel: next.localLabel,
          statusEffect: effect,
          kind: "status_window",
          postUrl: detail.status.sourceUrl,
          source: detail.status.source,
          postCreatedAt: null,
          alternative: null,
        });
        order.push(key);
        startEndSeen.add(seKey);
      }
    }
  }

  const nowMs = now.getTime();
  const slots: TimelineSlot[] = order.map((key) => {
    const raw = bySlotKey.get(key);
    if (!raw) {
      throw new Error(`missing timeline slot for key ${key}`);
    }
    const phase = phaseFor(raw.startMs, raw.endMs, nowMs);
    return {
      start: raw.start,
      end: raw.end,
      localLabel: raw.localLabel,
      statusEffect: raw.statusEffect,
      kind: raw.kind,
      postUrl: raw.postUrl,
      source: raw.source,
      phase,
      tier: tierFor(phase, raw.startMs, nowMs),
      effectLabel: effectLabelFor(raw.kind, raw.statusEffect),
      postCreatedAt: raw.postCreatedAt,
      alternative: raw.alternative,
    };
  });

  slots.sort((a, b) => {
    const aStart = new Date(a.start).getTime();
    const bStart = new Date(b.start).getTime();
    if (aStart !== bStart) {
      return aStart - bStart;
    }
    return new Date(a.end).getTime() - new Date(b.end).getTime();
  });

  return slots;
}

function pluralDays(days: number): string {
  return `${days} ${days === 1 ? "day" : "days"}`;
}

/** "in 5 hr 3 min" / "2 days ago" / "now" (within 45s either way). */
export function formatRelativeSpan(now: Date, target: Date): string {
  const diffMs = target.getTime() - now.getTime();
  if (Math.abs(diffMs) < 45000) {
    return "now";
  }

  const future = diffMs > 0;
  const elapsedMs = Math.abs(diffMs);
  const totalMinutes = future ? Math.max(1, Math.ceil(elapsedMs / 60000)) : Math.max(1, Math.floor(elapsedMs / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(pluralDays(days));
    if (hours > 0) {
      parts.push(`${hours} hr`);
    }
  } else {
    if (hours > 0) {
      parts.push(`${hours} hr`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} min`);
    }
    if (parts.length === 0) {
      parts.push("1 min");
    }
  }

  const text = parts.join(" ");
  return future ? `in ${text}` : `${text} ago`;
}

/** "Ended 2 hr 10 min ago" / "Ends in 40 min" / "Starts in 5 hr 3 min". */
export function slotRelative(slot: TimelineSlot, now: Date): string {
  if (slot.phase === "past") {
    return `Ended ${formatRelativeSpan(now, new Date(slot.end))}`;
  }
  if (slot.phase === "now") {
    return `Ends ${formatRelativeSpan(now, new Date(slot.end))}`;
  }
  return `Starts ${formatRelativeSpan(now, new Date(slot.start))}`;
}

function relativeMagnitude(now: Date, target: Date): { future: boolean; text: string; isNow: boolean } {
  const diffMs = target.getTime() - now.getTime();
  const future = diffMs >= 0;
  if (Math.abs(diffMs) < 45000) {
    return { future, text: "now", isNow: true };
  }
  const span = formatRelativeSpan(now, target);
  const text = future ? span.slice(3) : span.slice(0, -4);
  return { future, text, isNow: false };
}

/** Refreshes every `[data-rel-target][data-rel-kind]` element under `root` — called on a minute tick. */
/** "(in 3 hr 46 min)", "(ends in 40 min)", "(started 5 min ago)", "(ended 10 hr ago)" for timeline rows (R-628). */
export function parenthesisedRelative(now: Date, target: Date, kind: "window_start" | "window_end"): string {
  const { future, text, isNow } = relativeMagnitude(now, target);
  if (kind === "window_start") {
    return isNow ? "(starting now)" : future ? `(in ${text})` : `(started ${text} ago)`;
  }
  return isNow ? "(ending now)" : future ? `(ends in ${text})` : `(ended ${text} ago)`;
}

export function updateRelativeLabels(root: HTMLElement, now: Date): void {
  const elements = root.querySelectorAll<HTMLElement>("[data-rel-target][data-rel-kind]");
  elements.forEach((element) => {
    const targetIso = element.dataset.relTarget;
    const kind = element.dataset.relKind;
    if (!targetIso || !kind) {
      return;
    }
    const target = new Date(targetIso);
    if (Number.isNaN(target.getTime())) {
      return;
    }

    if (kind === "ago" || kind === "plain") {
      element.textContent = formatRelativeSpan(now, target);
      return;
    }

    const { future, text, isNow } = relativeMagnitude(now, target);
    if (kind === "power_on") {
      element.textContent = future || isNow ? "Power on" : `Power on for ${text}`;
      return;
    }
    if (kind === "window_start" || kind === "window_end") {
      element.textContent = parenthesisedRelative(now, target, kind);
      return;
    }
    if (kind === "starts_in") {
      element.textContent = isNow ? "Starts now" : future ? `Starts in ${text}` : `Started ${text} ago`;
    } else if (kind === "ends_in") {
      element.textContent = isNow ? "Ends now" : future ? `Ends in ${text}` : `Ended ${text} ago`;
    }
  });
}

/** "Power on for 6 hr 10 min · last window ended Sep 5, 11:00 PM PHT", or the cancellation wording, or null. */
export function powerSummary(slots: readonly TimelineSlot[], now: Date): string | null {
  if (slots.some((slot) => slot.phase === "now")) {
    return null;
  }

  let latest: TimelineSlot | null = null;
  let latestEndMs = Number.NEGATIVE_INFINITY;
  for (const slot of slots) {
    if (slot.phase !== "past") {
      continue;
    }
    const endMs = new Date(slot.end).getTime();
    if (endMs > latestEndMs) {
      latestEndMs = endMs;
      latest = slot;
    }
  }
  if (!latest) {
    return null;
  }

  const endDate = new Date(latest.end);
  if (latest.statusEffect === "cancelled") {
    const window: ApiWindow = { start: latest.start, end: latest.end, localLabel: latest.localLabel };
    return `Last planned window was cancelled (${formatManilaWindow(window)})`;
  }

  const span = relativeMagnitude(now, endDate).text;
  return `Power on for ${span} · last window ended ${formatManilaDateTime(endDate)}`;
}

/** R-627: short state for the Now row: "Power on for 10 hr 18 min", the cancelled note, or null when a window is current / nothing has ended. */
export function powerOnFor(slots: readonly TimelineSlot[], now: Date): { text: string; since: string } | null {
  if (slots.some((slot) => slot.phase === "now")) {
    return null;
  }
  let latest: TimelineSlot | null = null;
  let latestEndMs = Number.NEGATIVE_INFINITY;
  for (const slot of slots) {
    if (slot.phase !== "past") {
      continue;
    }
    const endMs = new Date(slot.end).getTime();
    if (endMs > latestEndMs) {
      latestEndMs = endMs;
      latest = slot;
    }
  }
  if (!latest) {
    return null;
  }
  if (latest.statusEffect === "cancelled") {
    return { text: "Last planned window was cancelled", since: latest.end };
  }
  return { text: `Power on for ${relativeMagnitude(now, new Date(latest.end)).text}`, since: latest.end };
}

function reportedLine(iso: string | null): string {
  if (!iso) {
    return "reported";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "reported";
  }
  return `reported ${formatManilaDateTime(date)}`;
}

function decoratePairChip(button: HTMLButtonElement, kind: "sources_differ" | "revised"): void {
  if (kind === "sources_differ") {
    const glyph = document.createElement("span");
    glyph.className = "pair-glyph";
    glyph.setAttribute("aria-hidden", "true");
    glyph.textContent = "⇄";
    button.appendChild(glyph);
    button.appendChild(document.createTextNode("Sources differ"));
    return;
  }
  button.textContent = "Revised";
}

function renderPairColumn(
  role: "winner" | "alt",
  source: "facebook" | "portal_sheet" | null,
  start: string,
  end: string,
  postCreatedAt: string | null,
  options: { showing?: boolean; strikeRange?: boolean; postUrl?: string | null },
): HTMLDivElement {
  const col = document.createElement("div");
  col.className = role === "winner" ? "pair-col pair-col-winner" : "pair-col pair-col-alt";

  const sourceEl = document.createElement("span");
  sourceEl.className = "pair-source";
  sourceEl.textContent = sourceLinkLabel(source);
  col.appendChild(sourceEl);

  const range = document.createElement("span");
  range.className = "pair-range";
  const clock = formatManilaClockRange({ start, end });
  if (options.strikeRange) {
    const struck = document.createElement("s");
    struck.textContent = clock;
    range.appendChild(struck);
  } else {
    range.textContent = clock;
  }
  col.appendChild(range);

  const reported = document.createElement("span");
  reported.className = "pair-reported";
  reported.textContent = reportedLine(postCreatedAt);
  col.appendChild(reported);

  if (options.showing) {
    const showing = document.createElement("span");
    showing.className = "pair-showing";
    showing.textContent = "Showing this";
    col.appendChild(showing);
  }

  if (options.postUrl) {
    const link = document.createElement("a");
    link.href = options.postUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = sourceLinkLabel(source);
    col.appendChild(link);
  }

  return col;
}

function renderSlotRow(slot: TimelineSlot, now: Date, isFocus: boolean, index: number): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "timeline-slot";
  li.dataset.phase = slot.phase;
  li.dataset.tier = slot.tier;
  if (isFocus) {
    li.dataset.focus = "true";
  }
  if (slot.phase === "now") {
    li.dataset.now = "true";
  }
  if (slot.alternative) {
    li.dataset.paired = slot.alternative.kind;
  }

  const marker = document.createElement("span");
  marker.className = "timeline-marker";
  marker.setAttribute("aria-hidden", "true");
  li.appendChild(marker);

  const when = document.createElement("p");
  when.className = "timeline-when";
  const dayName = document.createElement("span");
  dayName.className = "timeline-day-name";
  dayName.textContent = manilaDayLabel(new Date(slot.start), now);
  when.appendChild(dayName);
  when.appendChild(document.createTextNode(" "));
  const clock = document.createElement("span");
  clock.className = "timeline-clock";
  clock.textContent = formatManilaClockRange({ start: slot.start, end: slot.end });
  when.appendChild(clock);
  when.appendChild(document.createTextNode(" "));
  const rel = document.createElement("span");
  rel.className = "rel-time timeline-rel";
  const relKind: "window_start" | "window_end" = slot.phase === "future" ? "window_start" : "window_end";
  rel.dataset.relTarget = slot.phase === "future" ? slot.start : slot.end;
  rel.dataset.relKind = relKind;
  rel.textContent = parenthesisedRelative(now, new Date(rel.dataset.relTarget), relKind);
  when.appendChild(rel);
  const duration = formatWindowDuration({ start: slot.start, end: slot.end });
  if (duration) {
    when.appendChild(document.createTextNode(" "));
    const durationSpan = document.createElement("span");
    durationSpan.className = "timeline-duration";
    durationSpan.textContent = duration;
    when.appendChild(durationSpan);
  }
  li.appendChild(when);

  const title = document.createElement("p");
  title.className = "timeline-title";
  title.dataset.tier = slot.tier;
  title.textContent = slot.effectLabel;
  li.appendChild(title);

  if (slot.alternative) {
    const panelId = pairPanelId(slot.start, index);
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "pair-chip";
    chip.setAttribute("aria-expanded", "false");
    chip.setAttribute("aria-controls", panelId);
    decoratePairChip(chip, slot.alternative.kind);

    const panel = document.createElement("div");
    panel.className = "pair-panel";
    panel.id = panelId;
    panel.hidden = true;
    panel.appendChild(
      renderPairColumn("winner", slot.source, slot.start, slot.end, slot.postCreatedAt, { showing: true }),
    );
    panel.appendChild(
      renderPairColumn("alt", slot.alternative.source, slot.alternative.start, slot.alternative.end, slot.alternative.postCreatedAt, {
        strikeRange: slot.alternative.kind === "revised",
        postUrl: slot.alternative.postUrl,
      }),
    );

    chip.addEventListener("click", () => {
      const expanded = chip.getAttribute("aria-expanded") === "true";
      panel.hidden = expanded;
      chip.setAttribute("aria-expanded", expanded ? "false" : "true");
    });

    li.appendChild(chip);
    li.appendChild(panel);
  }

  if (slot.postUrl) {
    const link = document.createElement("a");
    link.href = slot.postUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = sourceLinkLabel(slot.source);
    li.appendChild(link);
  }

  return li;
}

function byStartAsc(a: TimelineSlot, b: TimelineSlot): number {
  return a.start < b.start ? -1 : a.start > b.start ? 1 : a.end < b.end ? -1 : a.end > b.end ? 1 : 0;
}

function byEndDesc(a: TimelineSlot, b: TimelineSlot): number {
  return a.end < b.end ? 1 : a.end > b.end ? -1 : byStartAsc(b, a);
}

function renderNowRow(slots: readonly TimelineSlot[], now: Date): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "timeline-now";
  li.dataset.timelineNow = "";

  const marker = document.createElement("span");
  marker.className = "timeline-marker timeline-marker-now";
  marker.setAttribute("aria-hidden", "true");
  li.appendChild(marker);

  const label = document.createElement("span");
  label.className = "timeline-now-label";
  label.textContent = "Now";
  li.appendChild(label);

  const state = document.createElement("span");
  state.className = "timeline-now-state";
  state.dataset.timelineNowState = "";
  const power = powerOnFor(slots, now);
  if (power) {
    state.textContent = power.text;
    if (power.text.startsWith("Power on for")) {
      state.classList.add("rel-time");
      state.dataset.relTarget = power.since;
      state.dataset.relKind = "power_on";
    }
  }
  li.appendChild(state);

  const said = document.createElement("span");
  said.className = "timeline-now-said";
  said.dataset.timelineNowSaid = "";
  li.appendChild(said);

  return li;
}

/**
 * R-627/R-628: one vertical timeline ordered by distance from now. Upcoming windows sit above the Now
 * row, soonest first; the Now row carries the current state; ended windows follow below it, latest
 * first; windows that started before yesterday fold into "Show n earlier windows". When a window is
 * current its row takes the Now position instead of a separate Now row.
 */
export function renderTimeline(container: HTMLElement, slots: readonly TimelineSlot[], now: Date): void {
  container.textContent = "";

  const heading = document.createElement("h3");
  heading.textContent = "Timeline";
  container.appendChild(heading);

  if (slots.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "No outage windows recorded for this area.";
    container.appendChild(empty);
    return;
  }

  const subtitle = document.createElement("p");
  subtitle.className = "section-sub";
  subtitle.textContent = "Above the Now line: what is coming, soonest first. Below it: what has ended, latest first.";
  container.appendChild(subtitle);

  const yesterdayStart = manilaDayStartMs(now, -1);
  const future = slots.filter((slot) => slot.phase === "future").sort(byStartAsc);
  const current = slots.filter((slot) => slot.phase === "now").sort(byStartAsc);
  const past = slots.filter((slot) => slot.phase === "past").sort(byEndDesc);
  const recentPast = past.filter((slot) => new Date(slot.start).getTime() >= yesterdayStart);
  const earlier = past.filter((slot) => new Date(slot.start).getTime() < yesterdayStart);

  const list = document.createElement("ol");
  list.className = "timeline-list timeline-vertical";

  future.forEach((slot, index) => {
    const isFocus = current.length === 0 && index === 0;
    list.appendChild(renderSlotRow(slot, now, isFocus, slots.indexOf(slot)));
  });

  if (current.length > 0) {
    current.forEach((slot, index) => {
      list.appendChild(renderSlotRow(slot, now, index === 0, slots.indexOf(slot)));
    });
  } else {
    list.appendChild(renderNowRow(slots, now));
  }

  for (const slot of recentPast) {
    list.appendChild(renderSlotRow(slot, now, false, slots.indexOf(slot)));
  }
  container.appendChild(list);

  if (earlier.length > 0) {
    const details = document.createElement("details");
    details.className = "timeline-earlier";
    const summary = document.createElement("summary");
    summary.textContent = `Show ${earlier.length} earlier ${earlier.length === 1 ? "window" : "windows"}`;
    details.appendChild(summary);
    const earlierList = document.createElement("ol");
    earlierList.className = "timeline-list timeline-vertical";
    for (const slot of earlier) {
      earlierList.appendChild(renderSlotRow(slot, now, false, slots.indexOf(slot)));
    }
    details.appendChild(earlierList);
    container.appendChild(details);
  }
}
